import React, { useState, useEffect } from 'react';
import { IoSunny, IoMoon } from 'react-icons/io5';
import { useTheme } from '@/context/ThemeContext';
import CollapsibleSection from '../../../components/CollapsibleSection';

const GeneralTab: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [timezone, setTimezone] = useState('America/New_York');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [textSize, setTextSize] = useState(3);
  const [highContrast, setHighContrast] = useState(false);
  const [screenReader, setScreenReader] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Phoenix', label: 'Mountain Time - Arizona (no DST)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  ];

  const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '12/31/2024' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '31/12/2024' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2024-12-31' },
  ];

  const textSizes = [
    { value: 1, label: 'Very Small' },
    { value: 2, label: 'Small' },
    { value: 3, label: 'Medium (Default)' },
    { value: 4, label: 'Large' },
    { value: 5, label: 'Very Large' },
  ];

  useEffect(() => {
    // Load saved settings from localStorage
    const savedTimezone = localStorage.getItem('timezone') || 'America/New_York';
    const savedDateFormat = localStorage.getItem('dateFormat') || 'MM/DD/YYYY';
    const savedTextSize = parseInt(localStorage.getItem('textSize') || '3');
    const savedHighContrast = localStorage.getItem('highContrast') === 'true';
    const savedScreenReader = localStorage.getItem('screenReader') === 'true';
    const savedReducedMotion = localStorage.getItem('reducedMotion') === 'true';

    setTimezone(savedTimezone);
    setDateFormat(savedDateFormat);
    setTextSize(savedTextSize);
    setHighContrast(savedHighContrast);
    setScreenReader(savedScreenReader);
    setReducedMotion(savedReducedMotion);
  }, []);

  const handleThemeChange = (newTheme: 'Light' | 'Dark') => {
    setTheme(newTheme);
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    try {
      // Save to localStorage
      localStorage.setItem('timezone', timezone);
      localStorage.setItem('dateFormat', dateFormat);
      localStorage.setItem('textSize', textSize.toString());
      localStorage.setItem('highContrast', highContrast.toString());
      localStorage.setItem('screenReader', screenReader.toString());
      localStorage.setItem('reducedMotion', reducedMotion.toString());

      // Apply text size
      document.documentElement.style.fontSize = `${14 + (textSize - 3) * 2}px`;

      // Apply high contrast
      if (highContrast) {
        document.documentElement.classList.add('high-contrast');
      } else {
        document.documentElement.classList.remove('high-contrast');
      }

      // Apply reduced motion
      if (reducedMotion) {
        document.documentElement.classList.add('reduced-motion');
      } else {
        document.documentElement.classList.remove('reduced-motion');
      }

      // Show success message
      await new Promise(resolve => setTimeout(resolve, 500));
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const getCurrentExample = () => {
    const format = dateFormats.find(f => f.value === dateFormat);
    return format ? format.example : '';
  };

  return (
    <div className="general-tab">
      <div>
        <h2 className="profile-form-section-title">General Settings</h2>
        <p className="profile-form-section-description">
          Customize your experience with theme preferences, timezone settings, and accessibility options.
        </p>
      </div>

      {/* Theme Settings */}
      <CollapsibleSection
        title="Appearance"
        description="Choose your preferred theme"
        defaultExpanded={true}
      >
        <div className="theme-section">
          <div className="theme-options">
            <button
              className={`theme-option ${theme === 'Light' ? 'active' : ''}`}
              onClick={() => handleThemeChange('Light')}
            >
              <div className="theme-option-icon light">
                <IoSunny />
              </div>
              <div className="theme-option-details">
                <h4 className="theme-option-title">Light Mode</h4>
                <p className="theme-option-description">
                  Clean and bright interface for daytime use
                </p>
              </div>
              <div className={`theme-option-radio ${theme === 'Light' ? 'active' : ''}`}>
                {theme === 'Light' && <div className="theme-option-radio-dot" />}
              </div>
            </button>

            <button
              className={`theme-option ${theme === 'Dark' ? 'active' : ''}`}
              onClick={() => handleThemeChange('Dark')}
            >
              <div className="theme-option-icon dark">
                <IoMoon />
              </div>
              <div className="theme-option-details">
                <h4 className="theme-option-title">Dark Mode</h4>
                <p className="theme-option-description">
                  Easier on the eyes for extended use and low-light environments
                </p>
              </div>
              <div className={`theme-option-radio ${theme === 'Dark' ? 'active' : ''}`}>
                {theme === 'Dark' && <div className="theme-option-radio-dot" />}
              </div>
            </button>
          </div>

          <div className="theme-preview">
            <h4 className="theme-preview-title">Preview</h4>
            <div className={`theme-preview-box ${theme.toLowerCase()}`}>
              <div className="theme-preview-header">
                <div className="theme-preview-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="theme-preview-title-bar">apply.ai</div>
              </div>
              <div className="theme-preview-content">
                <div className="theme-preview-sidebar"></div>
                <div className="theme-preview-main">
                  <div className="theme-preview-card"></div>
                  <div className="theme-preview-card small"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Timezone & Date Format */}
      <CollapsibleSection
        title="Regional Settings"
        description="Timezone and date format preferences"
        defaultExpanded={false}
      >
        <div className="profile-form-row">
          <div className="profile-form-field">
            <label className="profile-form-label">Timezone</label>
            <select
              className="profile-form-select"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            <p className="profile-form-helper-text">
              All dates and times will be displayed in your selected timezone
            </p>
          </div>
          <div className="profile-form-field">
            <label className="profile-form-label">Date Format</label>
            <select
              className="profile-form-select"
              value={dateFormat}
              onChange={(e) => setDateFormat(e.target.value)}
            >
              {dateFormats.map((format) => (
                <option key={format.value} value={format.value}>
                  {format.label}
                </option>
              ))}
            </select>
            <p className="profile-form-helper-text">
              Example: {getCurrentExample()}
            </p>
          </div>
        </div>
      </CollapsibleSection>

      {/* Accessibility Settings */}
      <CollapsibleSection
        title="Accessibility"
        description="Make the interface more accessible"
        defaultExpanded={false}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Text Size */}
          <div>
            <label className="profile-form-label" style={{ marginBottom: '1rem', display: 'block' }}>
              Text Size
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <input
                type="range"
                min="1"
                max="5"
                value={textSize}
                onChange={(e) => setTextSize(parseInt(e.target.value))}
                style={{
                  flex: 1,
                  accentColor: 'var(--accent-primary)'
                }}
              />
              <div style={{
                minWidth: '140px',
                fontSize: '0.9375rem',
                color: 'var(--text-secondary)',
                fontWeight: 500
              }}>
                {textSizes.find(s => s.value === textSize)?.label}
              </div>
            </div>
            <p className="profile-form-helper-text">
              Adjust the base font size for all text in the application
            </p>
          </div>

          {/* High Contrast Mode */}
          <div className="profile-form-toggle-section" style={{ paddingTop: 0, marginTop: 0, borderTop: 'none' }}>
            <div className="profile-form-toggle-header">
              <div>
                <div className="profile-form-toggle-title">High Contrast Mode</div>
                <div className="profile-form-toggle-description">
                  Increase contrast between text and backgrounds for better readability
                </div>
              </div>
              <label className="profile-toggle-switch">
                <input
                  type="checkbox"
                  checked={highContrast}
                  onChange={(e) => setHighContrast(e.target.checked)}
                />
                <span className="profile-toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Screen Reader Optimization */}
          <div className="profile-form-toggle-section">
            <div className="profile-form-toggle-header">
              <div>
                <div className="profile-form-toggle-title">Screen Reader Optimization</div>
                <div className="profile-form-toggle-description">
                  Optimize the interface for screen reader users with enhanced ARIA labels
                </div>
              </div>
              <label className="profile-toggle-switch">
                <input
                  type="checkbox"
                  checked={screenReader}
                  onChange={(e) => setScreenReader(e.target.checked)}
                />
                <span className="profile-toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Reduced Motion */}
          <div className="profile-form-toggle-section">
            <div className="profile-form-toggle-header">
              <div>
                <div className="profile-form-toggle-title">Reduce Motion</div>
                <div className="profile-form-toggle-description">
                  Minimize animations and transitions for users sensitive to motion
                </div>
              </div>
              <label className="profile-toggle-switch">
                <input
                  type="checkbox"
                  checked={reducedMotion}
                  onChange={(e) => setReducedMotion(e.target.checked)}
                />
                <span className="profile-toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Save Button */}
      <div className="profile-save-section">
        <button
          onClick={handleSaveSettings}
          className="profile-save-button"
          disabled={isSaving}
        >
          {isSaving ? 'Saving Settings...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default GeneralTab;
